import type { Draft, PayloadAction } from '@reduxjs/toolkit';

import type { AISidekickChatState, Conversation, Message } from './chatTypes';
import type {
  ConversationBranchSnapshot,
  CreateConversationBranchPayload,
} from './conversation-branch-types';

const clearTransientConversationState = (
  state: Draft<AISidekickChatState>
): void => {
  state.isStreaming = false;
  state.isComposerEngaged = false;
  state.isReconnecting = false;
  state.streamingContent = '';
  state.currentToolActivity = null;
  state.currentTraceId = null;
  state.error = null;
  state.retryAttempt = 0;
  state.retryingErrorId = null;
};

const buildActiveConversation = (
  state: Draft<AISidekickChatState>
): Conversation | null => {
  const { conversationId, conversationMetadata } = state;
  if (conversationId === null || conversationMetadata === null) {
    return null;
  }

  return {
    conversationId,
    messageCount: conversationMetadata.messageCount,
    createdAt: conversationMetadata.createdAt,
    updatedAt: conversationMetadata.updatedAt,
    messages: (state.messages as Message[]).filter(
      (message) => message.conversationId === conversationId
    ),
    ...(conversationMetadata.metadata !== undefined
      ? { metadata: conversationMetadata.metadata }
      : {}),
  };
};

const archiveActiveConversation = (
  state: Draft<AISidekickChatState>
): Conversation | null => {
  const conversation = buildActiveConversation(state);
  if (conversation === null) {
    return null;
  }

  const existing =
    state.branchSession.branchesByConversationId[conversation.conversationId];
  const snapshot: ConversationBranchSnapshot = {
    conversation,
    displayMessages: state.messages as Message[],
    errorEvents: state.errorEvents,
    displayAncestorCount: existing?.displayAncestorCount ?? 0,
    parentConversationId: existing?.parentConversationId ?? null,
    branchPointMessageId: existing?.branchPointMessageId ?? null,
  };
  state.branchSession.branchesByConversationId[conversation.conversationId] =
    snapshot as Draft<ConversationBranchSnapshot>;

  if (
    !state.branchSession.conversationOrder.includes(conversation.conversationId)
  ) {
    state.branchSession.conversationOrder.push(conversation.conversationId);
  }

  return conversation;
};

const restoreConversation = (
  state: Draft<AISidekickChatState>,
  snapshot: Draft<ConversationBranchSnapshot>
): void => {
  const { conversationId, messageCount, createdAt, updatedAt, metadata } =
    snapshot.conversation;

  state.conversationId = conversationId;
  state.messages = snapshot.displayMessages as Draft<Message>[];
  state.conversationMetadata = {
    messageCount,
    createdAt,
    updatedAt,
    ...(metadata !== undefined ? { metadata } : {}),
  };
  state.errorEvents = snapshot.errorEvents;
  state.branchSession.activeConversationId = conversationId;
  state.conversationScopeVersion += 1;
  clearTransientConversationState(state);
};

/**
 * Archives the active timeline and restores a known branch when no response is
 * in flight.
 */
export const adoptConversationBranch = (
  state: Draft<AISidekickChatState>,
  snapshot: Draft<ConversationBranchSnapshot>
): boolean => {
  if (state.isStreaming || state.pendingHostUserMessage !== null) {
    return false;
  }

  archiveActiveConversation(state);
  restoreConversation(state, snapshot);
  return true;
};

/** Adopts a newly created backend conversation as a local edited branch. */
export const createConversationBranchReducer = (
  state: Draft<AISidekickChatState>,
  action: PayloadAction<CreateConversationBranchPayload>
): void => {
  const {
    conversationId,
    createdAt,
    parentConversationId,
    branchPointMessageId,
    messages,
  } = action.payload;

  if (
    state.isStreaming ||
    state.pendingHostUserMessage !== null ||
    state.conversationId !== parentConversationId ||
    state.conversationMetadata === null ||
    conversationId === parentConversationId ||
    state.branchSession.branchesByConversationId[conversationId] !== undefined
  ) {
    return;
  }

  const parentConversation = archiveActiveConversation(state);
  if (parentConversation === null) {
    return;
  }

  if (state.branchSession.rootConversationId === null) {
    state.branchSession.rootConversationId = parentConversationId;
  }

  const conversation: Conversation = {
    conversationId,
    messages: [],
    messageCount: 0,
    createdAt,
    updatedAt: createdAt,
  };
  const copiedMessageIds = new Set(messages.map((message) => message.id));
  const inheritedErrorEvents = state.errorEvents
    .filter((event) => copiedMessageIds.has(event.sourceUserMessageId))
    .map((event) => ({ ...event, canRetry: false }));
  state.branchSession.branchesByConversationId[conversationId] = {
    conversation,
    displayMessages: messages,
    errorEvents: inheritedErrorEvents,
    displayAncestorCount: messages.length,
    parentConversationId,
    branchPointMessageId,
  } as Draft<ConversationBranchSnapshot>;
  state.branchSession.conversationOrder.push(conversationId);
  restoreConversation(
    state,
    state.branchSession.branchesByConversationId[conversationId]
  );
};

/** Removes an unsent branch and restores its parent conversation. */
export const discardConversationBranchReducer = (
  state: Draft<AISidekickChatState>,
  action: PayloadAction<string>
): void => {
  const conversationId = action.payload;
  const branch = state.branchSession.branchesByConversationId[conversationId];
  const parentConversationId = branch?.parentConversationId ?? null;
  const parent =
    parentConversationId === null
      ? undefined
      : state.branchSession.branchesByConversationId[parentConversationId];
  const shouldRestoreParent = state.conversationId === conversationId;
  if (
    state.branchSession.rootConversationId === conversationId ||
    branch === undefined ||
    (shouldRestoreParent && (state.isStreaming || parent === undefined))
  ) {
    return;
  }

  delete state.branchSession.branchesByConversationId[conversationId];
  const branchIndex =
    state.branchSession.conversationOrder.indexOf(conversationId);
  if (branchIndex !== -1) {
    state.branchSession.conversationOrder.splice(branchIndex, 1);
  }
  if (shouldRestoreParent && parent !== undefined) {
    restoreConversation(state, parent);
  }
};

/** Archives the active timeline and restores another local branch snapshot. */
export const switchConversationBranchReducer = (
  state: Draft<AISidekickChatState>,
  action: PayloadAction<string>
): void => {
  const targetConversationId = action.payload;
  if (
    targetConversationId === state.conversationId ||
    state.branchSession.branchesByConversationId[targetConversationId] ===
      undefined
  ) {
    return;
  }

  adoptConversationBranch(
    state,
    state.branchSession.branchesByConversationId[targetConversationId]
  );
};
